import {
  presencePreservingRotation,
  presencePreservingValue,
} from "./presencePreservingRotation";

/**
 * Write a block-attribute's WORLD `overrideRotation` back after a ROTATE or
 * MIRROR transform, per the DXF ATTRIB group-50 byte-faithful export
 * convention (`dxfBridge.attribExportPosition.ts` `resolveAttribExportGeometry`
 * WORLD-regime branch — `rotation: attr.overrideRotation` VERBATIM, no `?? 0`
 * fallback, so an EXPLICIT `0` emits a real `50\n0` group while an ABSENT
 * field omits it entirely; the two are NOT byte-identical even though both
 * resolve to the SAME effective angle at render time —
 * `attdefFromEntity.ts`'s `attr.overrideRotation ?? 0`).
 *
 * Preserves PRESENCE, not just value: an ALREADY-PRESENT `overrideRotation`
 * (any prior value, including an explicitly-authored `0`) is written back
 * EXPLICITLY regardless of what it transforms to — even when the transformed
 * value happens to be exactly 0. Only a field that was GENUINELY ABSENT
 * before this call, and that transforms to the SAME implicit default (0),
 * stays absent — matching the "materialize only when non-default" rule
 * `spreadSplineExtrusion` uses for the sibling Spline-extrusion case (never
 * invent a group the source never had), WITHOUT the sibling rule's converse
 * mistake of DISCARDING a group the source explicitly DID have.
 *
 * local Codex read-only review (P2, round 3) — the PRIOR version of this
 * function omitted the key whenever the TRANSFORMED value was 0, regardless
 * of whether it was present beforehand. `rotateEntityAngles`'s undo is a
 * blind angle-negation (`geometry/rotate-entities`'s inverse is `angleDeg:
 * -angleDeg`, no value snapshot, unlike MIRROR's `restoreAttributeRotations`
 * null-sentinel map) — so an ATTRIB imported with an explicit `50\n0` group,
 * ROTATEd to some angle and then undone (ROTATE by the negative angle) lands
 * back on `transformedRotation === 0`: the prior version deleted the field
 * instead of restoring the explicit `0` the source actually had, a discrete
 * byte-fidelity loss visible only on save. Preserving presence (not just
 * value) fixes this for ROTATE without needing a snapshot/restore map — the
 * angle arithmetic itself (`+angleDeg` then `-angleDeg`) is already exactly
 * self-inverse; only the presence-vs-value conflation was lossy.
 *
 * ONLY call this for a WORLD-placed attribute (`attr.overridePosition !==
 * undefined`). A block-local attribute's `overrideRotation` (when present —
 * a value-only EATTEDIT height/rotation edit seeds it from the ATTDEF
 * template WITHOUT ever setting `overridePosition`) is a DIFFERENT thing: it
 * rides the parent INSERT's own affine at render time
 * (`attdefFromEntity.ts resolvePlacement`'s block-local branch, `rotation:
 * attr?.overrideRotation ?? attdef.rotation` — composed with
 * `parentRotationDeg` downstream) and must be left completely untouched by
 * ROTATE/MIRROR — touching it here would double-count the INSERT's own
 * `rotation` field, which the caller already updates independently (local
 * Codex read-only review finding, round 1, on the MIRRTEXT-DIMENSION-MLEADER-
 * ATTRIB slice — see `rotateEntityAngles`'s and `geometryReducerEdit.ts`
 * mirror-entities' "Block" cases).
 *
 * The presence-vs-value rule itself lives in `presencePreservingRotation`
 * (round 4 — extracted once the Multileader `secondaryContexts[].context.
 * mTextData.rotation` site needed the identical byte-faithfulness contract
 * on a different field/entity type; ПРИОРИТЕТ #4 Этап 1 dup:check "one
 * domain truth, ≥2 occurrences" rule).
 */
export function withWorldAttributeRotation<
  T extends { overrideRotation?: number },
>(attr: T, transformedRotation: number): T {
  const value = presencePreservingRotation(
    attr.overrideRotation,
    transformedRotation,
  );
  if (value === undefined) {
    return attr;
  }
  return { ...attr, overrideRotation: value };
}

/**
 * GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the handedness
 * companion to {@link withWorldAttributeRotation}, for a block-attribute's
 * WORLD `overrideGenerationFlags` (DXF group 71, real ATTRIB field — same
 * presence-vs-value byte-faithfulness contract as `overrideRotation`: the
 * export encoder emits group 71 only when the field is PRESENT, so an
 * explicit `0` and an absent field are NOT byte-identical even though both
 * resolve to "no flip" at render time — `attdefFromEntity.ts`'s
 * `attr.overrideGenerationFlags ?? 0`). Same ONLY-call-for-WORLD-placed-
 * attributes caveat as the rotation sibling.
 */
export function withWorldAttributeGenerationFlags<
  T extends { overrideGenerationFlags?: number },
>(attr: T, transformedFlags: number): T {
  const value = presencePreservingValue(
    attr.overrideGenerationFlags,
    transformedFlags,
    0,
  );
  if (value === undefined) {
    return attr;
  }
  return { ...attr, overrideGenerationFlags: value };
}
